// Money text (SPEC §2.4, D-R8, D-R40). Built from integer minor units by string slicing; never
// from Intl currency output, whose symbols vary by ICU version.
import { SYMBOLS } from './currencies.js';
import { fromMinorUnits, toMinorUnits } from './convert.js';

const CODE_PATTERN = /^[A-Z]{3}$/;
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// en-US thousands: 1,234,567. Exported for rate.js; not part of the public API.
export function groupThousands(digits) {
  let out = '';
  for (let end = digits.length; end > 0; end -= 3) {
    const start = Math.max(0, end - 3);
    out = out === '' ? digits.slice(start, end) : `${digits.slice(start, end)},${out}`;
  }
  return out;
}

// Indian grouping (D-R40): the last three digits, then twos: 12,34,567
function groupIndian(digits) {
  if (digits.length <= 3) return digits;
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  let out = '';
  for (let end = head.length; end > 0; end -= 2) {
    const start = Math.max(0, end - 2);
    out = out === '' ? head.slice(start, end) : `${head.slice(start, end)},${out}`;
  }
  return `${out},${tail}`;
}

/**
 * The display form's currency mark (D-R8): a SYMBOLS entry attaches with no space ("£40.00",
 * "CA$50.00"); any other code is a prefix and a space ("AED 40.00"). `sign` goes before the mark.
 * Exported for rate.js; not part of the public API.
 */
export function withCurrencySign(sign, number, currency) {
  return hasOwn(SYMBOLS, currency) ? `${sign}${SYMBOLS[currency]}${number}` : `${sign}${currency} ${number}`;
}

/**
 * Formats an amount in major units.
 * - Display form (form, checkout, dashboard, portal): a SYMBOLS entry attaches with no space
 *   ("£40.00", "CA$50.00"); any other code is a prefix and a space ("AED 40.00", "KRW 50,000").
 *   `approx` prefixes "≈ ", except for USD, where ≈ never appears.
 * - PDF/ISO form (`isoPrefix`, PDFs only): "<CODE> <amount>", pure ASCII; `approx` reads
 *   "approx. GBP 40.00".
 * Throws RangeError on a garbage amount or code.
 */
export function formatMoney(amount, currency, opts = {}) {
  if (typeof currency !== 'string' || !CODE_PATTERN.test(currency)) {
    throw new RangeError('currency must be an upper-case ISO 4217 code');
  }
  const minor = toMinorUnits(amount, currency);
  const negative = minor < 0n;
  const text = fromMinorUnits(negative ? -minor : minor, currency);
  const dot = text.indexOf('.');
  const whole = dot === -1 ? text : text.slice(0, dot);
  const fraction = dot === -1 ? '' : text.slice(dot);
  const grouped = currency === 'INR' ? groupIndian(whole) : groupThousands(whole);
  const number = `${grouped}${fraction}`;
  const sign = negative ? '-' : '';
  const approx = opts.approx === true && currency !== 'USD';

  if (opts.isoPrefix === true) {
    return `${approx ? 'approx. ' : ''}${currency} ${sign}${number}`;
  }
  const body = withCurrencySign(`${sign}`, number, currency);
  return approx ? `≈ ${body}` : body;
}
